import React, { useEffect, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "../../../components/ui/dialog";
import { Plus, Trash2, Edit3, Globe, Settings, Save, X, MapPin, Calendar, Building2, ArrowLeft, AlertCircle, CheckCircle, Type, Link, Key, Hash, Code, Play, Loader2, Eye, EyeOff, ChevronDown, Lightbulb, Wrench, Info } from "lucide-react";

const { ipcRenderer } = window.require('electron');

export const APIFunctionsDialog = ({
	isOpen,
	onClose,
	onReturnToMain,
	instanceId,
	onSave,
}) => {
	const [functions, setFunctions] = useState([]);
	const [isCreating, setIsCreating] = useState(false);
	const [editingFunction, setEditingFunction] = useState(null);
	const [newFunction, setNewFunction] = useState({
		name: "",
		description: "",
		url: "",
		method: "GET",
		parameters: [],
		headers: []
	});
	const [errorMessage, setErrorMessage] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [testingFunction, setTestingFunction] = useState(null);
	const [testParams, setTestParams] = useState({});
	const [testResult, setTestResult] = useState(null);
	const [isTesting, setIsTesting] = useState(false);
	const [showExamples, setShowExamples] = useState(false);

	// Carrega functions salvas quando o modal abre
	useEffect(() => {
		if (isOpen && instanceId) {
			loadFunctions();
		}
	}, [isOpen, instanceId]);

	// Auto-clear messages after some time
	useEffect(() => {
		if (errorMessage) {
			const timer = setTimeout(() => setErrorMessage(""), 5000);
			return () => clearTimeout(timer);
		}
	}, [errorMessage]);

	useEffect(() => {
		if (successMessage) {
			const timer = setTimeout(() => setSuccessMessage(""), 3000);
			return () => clearTimeout(timer);
		}
	}, [successMessage]);

	// Clear messages when starting to create or edit
	useEffect(() => {
		if (isCreating || editingFunction) {
			setErrorMessage("");
			setSuccessMessage("");
		}
	}, [isCreating, editingFunction]);

	const loadFunctions = async () => {
		try {
			const response = await ipcRenderer.invoke('list-functions', { instanceId });
			if (response.success) {
				setFunctions(response.functions || []);
			} else {
				console.error('Erro ao carregar functions:', response.error);
				setFunctions([]);
			}
		} catch (error) {
			console.error('Erro ao carregar functions:', error);
			setFunctions([]);
		}
	};

	// Função auxiliar para recarregar as functions após mudanças
	const refreshFunctions = async () => {
		await loadFunctions();
		// Notifica o componente pai sobre as mudanças
		if (onSave) {
			onSave(functions);
		}
	};

	const getExampleFunction = (type) => {
		const examples = {
			verificar_cep: {
				name: "verificar_cep",
				description: "Consulta informações de endereço através do CEP usando a BrasilAPI",
				url: "https://brasilapi.com.br/api/cep/v1/{cep}",
				method: "GET",
				parameters: [
					{
						name: "cep",
						type: "string",
						location: "path",
						required: true,
						description: "CEP para consulta (formato: 01310100)"
					}
				]
			},
			consultar_feriados: {
				name: "consultar_feriados",
				description: "Consulta os feriados nacionais do Brasil para um ano específico",
				url: "https://brasilapi.com.br/api/feriados/v1/{ano}",
				method: "GET",
				parameters: [
					{
						name: "ano",
						type: "string",
						location: "path",
						required: true,
						description: "Ano para consulta dos feriados (formato: 2024)"
					}
				]
			},
			consultar_cnpj: {
				name: "consultar_cnpj",
				description: "Consulta dados de empresa através do CNPJ usando a BrasilAPI",
				url: "https://brasilapi.com.br/api/cnpj/v1/{cnpj}",
				method: "GET",
				parameters: [
					{
						name: "cnpj",
						type: "string",
						location: "path",
						required: true,
						description: "CNPJ da empresa (apenas números, 14 dígitos)"
					}
				]
			}
		};
		return examples[type];
	};

	// Adiciona uma function de exemplo
	const addExampleFunction = async (type) => {
		const exampleFunction = getExampleFunction(type);
		if (!exampleFunction) return;

		try {
			const response = await ipcRenderer.invoke('create-function', {
				instanceId,
				functionData: exampleFunction
			});

			if (response.success) {
				await refreshFunctions();
				setSuccessMessage(`✅ Function "${exampleFunction.name}" adicionada com sucesso!`);
			} else {
				setErrorMessage('Erro ao adicionar function: ' + response.error);
			}
		} catch (error) {
			console.error('Erro ao adicionar function de exemplo:', error);
			setErrorMessage('Erro interno ao adicionar function: ' + error.message);
		}
	};

	const normalizeToSnakeCase = (name) => {
		if (!name || typeof name !== 'string') return '';
		return name
			.toLowerCase()
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.replace(/[^a-z0-9\s_]/g, '')
			.replace(/\s+/g, '_')
			.replace(/_{2,}/g, '_')
			.replace(/^_|_$/g, '')
			.substring(0, 60);
	};

	const validateFunction = (func) => {
		const errors = [];

		if (!func.name.trim()) {
			errors.push("Nome da função é obrigatório");
		} else {
			const normalized = normalizeToSnakeCase(func.name);
			if (!normalized || normalized.length < 2) {
				errors.push("Nome deve conter pelo menos 2 caracteres válidos");
			}
		}

		if (!func.description.trim()) {
			errors.push("Descrição é obrigatória");
		}

		if (!func.url.trim()) {
			errors.push("URL é obrigatória");
		}

		if (!func.url.startsWith('http://') && !func.url.startsWith('https://')) {
			errors.push("URL deve começar com http:// ou https://");
		}

		func.parameters.forEach((param, index) => {
			if (!param.name.trim()) {
				errors.push(`Parâmetro ${index + 1}: Nome é obrigatório`);
			}
		});

		return errors;
	};

	const handleCreateFunction = async () => {
		const errors = validateFunction(newFunction);
		if (errors.length > 0) {
			setErrorMessage('Erros encontrados:\n' + errors.join('\n'));
			return;
		}

		// Verifica se já existe function com mesmo nome
		if (functions.some(f => f.name === newFunction.name)) {
			setErrorMessage('Já existe uma função com este nome');
			return;
		}

		try {
			const response = await ipcRenderer.invoke('create-function', {
				instanceId,
				functionData: newFunction
			});

			if (response.success) {
				setSuccessMessage('Function criada com sucesso!');
				await refreshFunctions();
				setIsCreating(false);
				setNewFunction({
					name: "",
					description: "",
					url: "",
					method: "GET",
					parameters: [],
					headers: []
				});
			} else {
				if (response.validationErrors) {
					setErrorMessage('Erros de validação:\n' + response.validationErrors.join('\n'));
				} else {
					setErrorMessage('Erro ao criar function: ' + response.error);
				}
			}
		} catch (error) {
			console.error('Erro ao criar function:', error);
			setErrorMessage('Erro interno ao criar function: ' + error.message);
		}
	};

	const handleEditFunction = (func) => {
		setEditingFunction({ ...func, headers: func.headers || [] });
	};

	const handleUpdateFunction = async () => {
		const errors = validateFunction(editingFunction);
		if (errors.length > 0) {
			setErrorMessage('Erros encontrados:\n' + errors.join('\n'));
			return;
		}

		try {
			const response = await ipcRenderer.invoke('update-function', {
				instanceId,
				functionId: editingFunction.id,
				functionData: editingFunction
			});

			if (response.success) {
				setSuccessMessage('Function atualizada com sucesso!');
				await refreshFunctions();
				setEditingFunction(null);
			} else {
				if (response.validationErrors) {
					setErrorMessage('Erros de validação:\n' + response.validationErrors.join('\n'));
				} else {
					setErrorMessage('Erro ao atualizar function: ' + response.error);
				}
			}
		} catch (error) {
			console.error('Erro ao atualizar function:', error);
			setErrorMessage('Erro interno ao atualizar function: ' + error.message);
		}
	};

	const handleDeleteFunction = async (functionId) => {
		if (confirm('Tem certeza que deseja excluir esta função?')) {
			try {
				const response = await ipcRenderer.invoke('delete-function', {
					instanceId,
					functionId
				});

				if (response.success) {
					setSuccessMessage('Function excluída com sucesso!');
					await refreshFunctions();
				} else {
					setErrorMessage('Erro ao excluir function: ' + response.error);
				}
			} catch (error) {
				console.error('Erro ao excluir function:', error);
				setErrorMessage('Erro interno ao excluir function: ' + error.message);
			}
		}
	};

	const addParameter = (targetFunction, setTargetFunction) => {
		const newParam = {
			name: "",
			type: "string",
			location: "query",
			required: false,
			description: ""
		};

		setTargetFunction({
			...targetFunction,
			parameters: [...targetFunction.parameters, newParam]
		});
	};

	const removeParameter = (targetFunction, setTargetFunction, index) => {
		setTargetFunction({
			...targetFunction,
			parameters: targetFunction.parameters.filter((_, i) => i !== index)
		});
	};

	const updateParameter = (targetFunction, setTargetFunction, index, field, value) => {
		const updatedParams = targetFunction.parameters.map((param, i) =>
			i === index ? { ...param, [field]: value } : param
		);

		setTargetFunction({
			...targetFunction,
			parameters: updatedParams
		});
	};

	const addHeader = (targetFunction, setTargetFunction) => {
		const newHeader = {
			name: "",
			value: "",
			isSecret: false
		};
		setTargetFunction({
			...targetFunction,
			headers: [...(targetFunction.headers || []), newHeader]
		});
	};

	const removeHeader = (targetFunction, setTargetFunction, index) => {
		setTargetFunction({
			...targetFunction,
			headers: (targetFunction.headers || []).filter((_, i) => i !== index)
		});
	};

	const updateHeader = (targetFunction, setTargetFunction, index, field, value) => {
		const updatedHeaders = (targetFunction.headers || []).map((header, i) =>
			i === index ? { ...header, [field]: value } : header
		);
		setTargetFunction({
			...targetFunction,
			headers: updatedHeaders
		});
	};

	const handleTestFunction = async (func) => {
		setTestingFunction(func);
		setTestParams({});
		setTestResult(null);
	};

	const executeTest = async () => {
		if (!testingFunction) return;

		setIsTesting(true);
		setTestResult(null);

		try {
			const response = await ipcRenderer.invoke('test-function', {
				instanceId,
				functionName: testingFunction.name,
				parameters: testParams
			});
			setTestResult(response);
		} catch (error) {
			setTestResult({
				success: false,
				error: error.message
			});
		} finally {
			setIsTesting(false);
		}
	};

	const closeTestModal = () => {
		setTestingFunction(null);
		setTestParams({});
		setTestResult(null);
	};

	const renderParameterForm = (targetFunction, setTargetFunction) => (
		<div>
			<div className="flex items-center justify-between mb-3">
				<div className="flex items-center gap-2">
					<Settings className="w-4 h-4 text-amber-400" />
					<label className="text-sm font-medium text-textPrimary">
						Parâmetros
					</label>
					<span className="text-grayText text-xs">(Opcional)</span>
				</div>
				<button
					type="button"
					onClick={() => addParameter(targetFunction, setTargetFunction)}
					className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-amber-500/20 text-amber-300 rounded-lg border border-amber-500/30 hover:bg-amber-500/30 transition-colors"
				>
					<Plus className="h-3 w-3" />
					<span>Adicionar</span>
				</button>
			</div>

			{targetFunction.parameters.length === 0 ? (
				<div className="text-center py-4 text-grayText bg-grayContent/20 border border-dashed border-amber-500/20 rounded-lg">
					<Settings className="w-6 h-6 mx-auto mb-1 opacity-50" />
					<p className="text-xs">Nenhum parâmetro configurado</p>
				</div>
			) : (
				<div className="space-y-2">
					{targetFunction.parameters.map((param, index) => (
						<div key={index} className="p-3 rounded-lg border border-amber-500/20 bg-whiteBase/5">
							<div className="flex items-center justify-between mb-2">
								<div className="flex items-center gap-2">
									<span className="w-5 h-5 rounded bg-amber-500/20 flex items-center justify-center text-amber-400 text-xs font-bold">{index + 1}</span>
									<span className="text-textPrimary text-sm font-medium">Parâmetro</span>
								</div>
								<button
									type="button"
									onClick={() => removeParameter(targetFunction, setTargetFunction, index)}
									className="p-1 text-red-400 hover:bg-red-500/20 rounded transition-colors"
								>
									<Trash2 className="h-3.5 w-3.5" />
								</button>
							</div>

							<div className="grid grid-cols-2 gap-2 mb-2">
								<div>
									<label className="text-xs text-grayText mb-1 block">Nome</label>
									<input
										type="text"
										placeholder="cep"
										value={param.name}
										onChange={(e) => updateParameter(targetFunction, setTargetFunction, index, 'name', e.target.value)}
										className="w-full rounded-lg border border-amber-500/20 bg-grayContent/30 px-2.5 py-1.5 text-textPrimary text-sm focus:border-amber-500/40 focus:ring focus:ring-amber-500/20 placeholder-grayText transition-all"
									/>
								</div>
								<div>
									<label className="text-xs text-grayText mb-1 block">Localização</label>
									<select
										value={param.location || 'query'}
										onChange={(e) => updateParameter(targetFunction, setTargetFunction, index, 'location', e.target.value)}
										className="w-full rounded-lg border border-amber-500/20 bg-grayContent/30 px-2.5 py-1.5 text-textPrimary text-sm focus:border-amber-500/40 focus:ring focus:ring-amber-500/20 appearance-none cursor-pointer transition-all"
										style={{
											backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
											backgroundPosition: 'right 0.5rem center',
											backgroundRepeat: 'no-repeat',
											backgroundSize: '1.2em 1.2em'
										}}
									>
										<option value="path" className="bg-grayBase">Path - substitui {'{param}'} na URL</option>
										<option value="query" className="bg-grayBase">Query - adiciona ?param=valor</option>
										{['POST', 'PUT'].includes(targetFunction.method) && (
											<option value="body" className="bg-grayBase">Body - envia no corpo JSON</option>
										)}
									</select>
								</div>
							</div>

							<label className="flex items-center gap-2 text-xs text-textSecondary">
								<input
									type="checkbox"
									checked={param.required}
									onChange={(e) => updateParameter(targetFunction, setTargetFunction, index, 'required', e.target.checked)}
									className="rounded border-amber-500/30 bg-grayContent/30 text-amber-500 focus:ring-amber-500/30"
								/>
								<span>Obrigatório</span>
							</label>
						</div>
					))}
				</div>
			)}
		</div>
	);

	const renderHeadersForm = (targetFunction, setTargetFunction) => (
		<div>
			<div className="flex items-center justify-between mb-3">
				<div className="flex items-center gap-2">
					<Key className="w-4 h-4 text-amber-400" />
					<label className="text-sm font-medium text-textPrimary">
						Headers HTTP
					</label>
					<span className="text-grayText text-xs">(Opcional)</span>
				</div>
				<button
					type="button"
					onClick={() => addHeader(targetFunction, setTargetFunction)}
					className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-amber-500/20 text-amber-300 rounded-lg border border-amber-500/30 hover:bg-amber-500/30 transition-colors"
				>
					<Plus className="h-3 w-3" />
					<span>Adicionar</span>
				</button>
			</div>

			{(!targetFunction.headers || targetFunction.headers.length === 0) ? (
				<div className="text-center py-4 text-grayText bg-grayContent/20 border border-dashed border-amber-500/20 rounded-lg">
					<Key className="w-6 h-6 mx-auto mb-1 opacity-50 text-amber-400" />
					<p className="text-xs">Nenhum header configurado</p>
				</div>
			) : (
				<div className="space-y-2">
					{targetFunction.headers.map((header, index) => (
						<div key={index} className="p-3 rounded-lg border border-amber-500/20 bg-whiteBase/5">
							<div className="flex items-center justify-between mb-2">
								<div className="flex items-center gap-2">
									<span className="w-5 h-5 rounded bg-amber-500/20 flex items-center justify-center text-amber-400 text-xs font-bold">{index + 1}</span>
									<span className="text-textPrimary text-sm font-medium">Header</span>
								</div>
								<button
									type="button"
									onClick={() => removeHeader(targetFunction, setTargetFunction, index)}
									className="p-1 text-red-400 hover:bg-red-500/20 rounded transition-colors"
								>
									<Trash2 className="h-3.5 w-3.5" />
								</button>
							</div>

							<div className="grid grid-cols-2 gap-2 mb-2">
								<div>
									<label className="text-xs text-grayText mb-1 block">Nome</label>
									<input
										type="text"
										placeholder="Authorization"
										value={header.name}
										onChange={(e) => updateHeader(targetFunction, setTargetFunction, index, 'name', e.target.value)}
										className="w-full rounded-lg border border-amber-500/20 bg-grayContent/30 px-2.5 py-1.5 text-textPrimary text-sm focus:border-amber-500/40 focus:ring focus:ring-amber-500/20 placeholder-grayText transition-all"
									/>
								</div>
								<div>
									<label className="text-xs text-grayText mb-1 block">Valor</label>
									<div className="relative">
										<input
											type={header.isSecret ? "password" : "text"}
											placeholder="Bearer token..."
											value={header.value}
											onChange={(e) => updateHeader(targetFunction, setTargetFunction, index, 'value', e.target.value)}
											className="w-full rounded-lg border border-amber-500/20 bg-grayContent/30 px-2.5 py-1.5 pr-8 text-textPrimary text-sm focus:border-amber-500/40 focus:ring focus:ring-amber-500/20 placeholder-grayText transition-all"
										/>
										<button
											type="button"
											onClick={() => updateHeader(targetFunction, setTargetFunction, index, 'isSecret', !header.isSecret)}
											className="absolute right-2 top-1/2 -translate-y-1/2 text-grayText hover:text-amber-400 transition-colors"
										>
											{header.isSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
										</button>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);

	const renderFunctionForm = (func, setFunc, onSave, onCancel, isEditing = false) => (
		<div className="space-y-5">
			{/* Header do formulário */}
			<div className="flex items-center gap-3 pb-4 border-b border-amber-500/20">
				<div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/30 via-orange-500/25 to-yellow-500/30 border border-amber-500/50">
					{isEditing ? (
						<Edit3 className="w-5 h-5 text-amber-300" />
					) : (
						<Code className="w-5 h-5 text-amber-300" />
					)}
				</div>
				<div>
					<h3 className="text-textPrimary font-semibold">
						{isEditing ? 'Editar Função' : 'Nova Função'}
					</h3>
					<p className="text-textSecondary text-sm">
						{isEditing ? 'Modifique as configurações da função' : 'Defina como a IA irá chamar sua API'}
					</p>
				</div>
			</div>

			<div className="grid grid-cols-2 gap-4">
				<div>
					<label className="block text-sm font-medium text-textPrimary mb-2">
						Nome da Função <span className="text-redBase">*</span>
					</label>
					<input
						type="text"
						value={func.name}
						onChange={(e) => setFunc({ ...func, name: e.target.value })}
						className="w-full rounded-lg border border-amber-500/25 bg-grayContent/30 backdrop-blur-xl px-3 py-2.5 text-textPrimary focus:border-amber-500/50 focus:ring focus:ring-amber-500/30 placeholder-grayText transition-all"
						placeholder="Consultar CEP"
					/>
					{func.name && (
						<p className="text-xs text-emeraldLight mt-1">
							Nome final: <code className="bg-emeraldPrimary/10 px-1.5 py-0.5 rounded font-mono">{normalizeToSnakeCase(func.name) || '...'}</code>
						</p>
					)}
					{!func.name && (
						<p className="text-xs text-grayText mt-1">
							Será convertido para snake_case automaticamente
						</p>
					)}
				</div>

				<div>
					<label className="block text-sm font-medium text-textPrimary mb-2">
						Método HTTP <span className="text-redBase">*</span>
					</label>
					<select
						value={func.method}
						onChange={(e) => setFunc({ ...func, method: e.target.value })}
						className="w-full rounded-lg border border-amber-500/25 bg-grayContent/30 backdrop-blur-xl px-3 py-2.5 text-textPrimary focus:border-amber-500/50 focus:ring focus:ring-amber-500/30 appearance-none cursor-pointer transition-all"
						style={{
							backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
							backgroundPosition: 'right 0.5rem center',
							backgroundRepeat: 'no-repeat',
							backgroundSize: '1.5em 1.5em'
						}}
					>
						<option value="GET" className="bg-grayBase text-textPrimary">GET - Consultar dados</option>
						<option value="POST" className="bg-grayBase text-textPrimary">POST - Enviar dados</option>
					</select>
				</div>
			</div>

			<div>
				<label className="block text-sm font-medium text-textPrimary mb-2">
					Descrição <span className="text-redBase">*</span>
				</label>
				<textarea
					value={func.description}
					onChange={(e) => setFunc({ ...func, description: e.target.value })}
					className="w-full h-20 rounded-lg border border-amber-500/25 bg-grayContent/30 backdrop-blur-xl px-3 py-2.5 text-textPrimary focus:border-amber-500/50 focus:ring focus:ring-amber-500/30 placeholder-grayText transition-all resize-none"
					placeholder="Descreva o que esta função faz. Ex: Consulta informações de endereço através do CEP brasileiro"
				/>
				<p className="text-xs text-grayText mt-1">
					Seja específico sobre o que a função retorna
				</p>
			</div>

			<div>
				<label className="block text-sm font-medium text-textPrimary mb-2">
					URL da API <span className="text-redBase">*</span>
				</label>
				<input
					type="url"
					value={func.url}
					onChange={(e) => setFunc({ ...func, url: e.target.value })}
					className="w-full rounded-lg border border-amber-500/25 bg-grayContent/30 backdrop-blur-xl px-3 py-2.5 text-textPrimary focus:border-amber-500/50 focus:ring focus:ring-amber-500/30 placeholder-grayText transition-all"
					placeholder="https://viacep.com.br/ws/{cep}/json/"
				/>
				<p className="text-xs text-grayText mt-1">
					Use {`{parametro}`} para parâmetros de path
				</p>
			</div>

			{renderParameterForm(func, setFunc)}

			{renderHeadersForm(func, setFunc)}

			{/* Mensagens de erro e sucesso */}
			{errorMessage && (
				<div className="bg-redBase/10 border border-redBase/30 rounded-lg p-3">
					<div className="flex items-start gap-2">
						<AlertCircle className="w-4 h-4 text-redBase flex-shrink-0 mt-0.5" />
						<p className="text-redBase text-sm flex-1 whitespace-pre-line">{errorMessage}</p>
						<button
							type="button"
							onClick={() => setErrorMessage("")}
							className="p-1 hover:bg-redBase/20 rounded transition-colors"
						>
							<X className="w-3 h-3 text-redBase" />
						</button>
					</div>
				</div>
			)}

			{successMessage && (
				<div className="bg-emeraldPrimary/10 border border-emeraldPrimary/30 rounded-lg p-3">
					<div className="flex items-start gap-2">
						<CheckCircle className="w-4 h-4 text-emeraldLight flex-shrink-0 mt-0.5" />
						<p className="text-emeraldLight text-sm flex-1">{successMessage}</p>
						<button
							type="button"
							onClick={() => setSuccessMessage("")}
							className="p-1 hover:bg-emeraldPrimary/20 rounded transition-colors"
						>
							<X className="w-3 h-3 text-emeraldLight" />
						</button>
					</div>
				</div>
			)}

			<div className="flex gap-3 pt-4 border-t border-amber-500/20">
				<button
					type="button"
					onClick={onCancel}
					className="flex-1 flex items-center justify-center gap-2 rounded-lg text-textPrimary px-4 py-2.5 font-medium transition-all bg-gradient-to-r from-dangerFrom to-dangerTo hover:from-redMedium hover:to-redBase"
				>
					<X className="h-4 w-4" />
					<span>Cancelar</span>
				</button>
				<button
					onClick={onSave}
					type="button"
					className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-amber-500/80 to-orange-500/80 border border-amber-400/60 px-4 py-2.5 text-sm font-semibold text-textPrimary transition-all hover:from-amber-500/90 hover:to-orange-500/90"
				>
					<Save className="h-4 w-4" />
					<span>{isEditing ? 'Atualizar' : 'Criar Função'}</span>
				</button>
			</div>
		</div>
	);

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent
				className="max-w-[850px] h-[85vh] bg-gradient-to-br from-modalBg via-modalBgSecondary to-modalBg backdrop-blur-3xl border border-modalBorder shadow-2xl shadow-blackBase/50 relative overflow-hidden flex flex-col"
				style={{
					position: 'fixed',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					zIndex: 10000
				}}
			>
				{/* Efeitos de fundo glassmorphism */}
				<div className="absolute inset-0 bg-gradient-to-br from-modalOverlaySoft via-transparent to-modalOverlayStrong opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
				<div className="absolute top-16 left-24 w-32 h-32 bg-amber-500/10 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '8s' }}></div>
				<div className="absolute bottom-16 right-16 w-36 h-36 bg-orange-500/8 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '9s', animationDelay: '2s' }}></div>

				{/* Botão X customizado */}
				<button
					type="button"
					onClick={onClose}
					className="absolute right-4 top-4 z-50 flex items-center justify-center w-8 h-8 rounded-full bg-modalOverlayMedium backdrop-blur-xl border border-modalOverlayStrong text-textOnDarkMedium hover:text-textPrimary hover:bg-modalOverlayStrong transition-all duration-300 hover:scale-110"
				>
					<X className="w-4 h-4" />
				</button>

				<DialogHeader className="relative z-10 flex-shrink-0 px-6 pt-6">
					<DialogTitle className="flex items-center gap-4 text-textPrimary">
						<div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/40 via-orange-500/30 to-yellow-500/40 shadow-lg border border-amber-500/60">
							<Wrench className="w-6 h-6 text-amber-300 drop-shadow-sm" />
							<div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-orange-500/8 rounded-xl animate-pulse-subtle"></div>
						</div>
						<div>
							<h2 className="text-xl font-semibold">Ferramentas de API</h2>
							<p className="text-sm text-textSecondary font-normal">Configure APIs externas que a IA pode chamar</p>
						</div>
					</DialogTitle>
				</DialogHeader>

				{/* Conteúdo com scroll */}
				<div className="flex-1 overflow-y-auto px-6 pb-2 relative z-10">
					<div className="flex flex-col gap-6 py-4">
						{/* Mensagens gerais de erro e sucesso */}
						{errorMessage && !isCreating && !editingFunction && (
							<div className="bg-redBase/10 border border-redBase/30 rounded-lg p-3 backdrop-blur-xl">
								<div className="flex items-start gap-2">
									<AlertCircle className="w-4 h-4 text-redBase flex-shrink-0 mt-0.5" />
									<div className="flex-1">
										<p className="text-redBase text-sm whitespace-pre-line">{errorMessage}</p>
									</div>
									<button
										type="button"
										onClick={() => setErrorMessage("")}
										className="p-1 rounded hover:bg-redBase/20 transition-colors"
									>
										<X className="w-3 h-3 text-redBase" />
									</button>
								</div>
							</div>
						)}

						{successMessage && !isCreating && !editingFunction && (
							<div className="bg-emeraldPrimary/10 border border-emeraldPrimary/30 rounded-lg p-3 backdrop-blur-xl">
								<div className="flex items-start gap-2">
									<CheckCircle className="w-4 h-4 text-emeraldLight flex-shrink-0 mt-0.5" />
									<div className="flex-1">
										<p className="text-emeraldLight text-sm">{successMessage}</p>
									</div>
									<button
										type="button"
										onClick={() => setSuccessMessage("")}
										className="p-1 rounded hover:bg-emeraldPrimary/20 transition-colors"
									>
										<X className="w-3 h-3 text-emeraldLight" />
									</button>
								</div>
							</div>
						)}

						{/* Seção de informações */}
						<div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-3">
							<div className="flex items-center text-amber-400">
								<Info className="h-5 w-5 mr-2" />
								<span className="font-medium">Como funciona</span>
							</div>

							<div className="space-y-2 text-sm">
								<div className="flex items-start gap-2">
									<span className="text-amber-400 font-bold">1.</span>
									<p className="text-amber-200">
										<strong>Cadastre a função</strong> — Configure a URL da API e os parâmetros necessários
									</p>
								</div>
								<div className="flex items-start gap-2">
									<span className="text-amber-400 font-bold">2.</span>
									<p className="text-amber-200">
										<strong>Converse normalmente</strong> — A IA detecta automaticamente quando usar cada função
									</p>
								</div>
								<div className="flex items-start gap-2">
									<span className="text-amber-400 font-bold">3.</span>
									<p className="text-amber-200">
										<strong>Receba dados em tempo real</strong> — A IA consulta a API e responde com os dados
									</p>
								</div>
							</div>

							<div className="pt-2 border-t border-amber-500/20">
								<p className="text-xs text-amber-300/80 mb-2">Exemplos de perguntas que acionam as funções:</p>
								<div className="flex flex-wrap gap-2">
									<span className="px-2 py-1 bg-amber-500/20 rounded text-xs text-amber-200">"Qual o endereço do CEP 01310100?"</span>
									<span className="px-2 py-1 bg-amber-500/20 rounded text-xs text-amber-200">"Quais são os feriados de 2025?"</span>
									<span className="px-2 py-1 bg-amber-500/20 rounded text-xs text-amber-200">"Consulta o CNPJ 00000000000191"</span>
								</div>
							</div>
						</div>

						{/* Exemplos populares - Collapsible */}
						{!isCreating && !editingFunction && (
							<div className="bg-grayContent/30 border border-amber-500/20 rounded-lg overflow-hidden">
								<button
									type="button"
									onClick={() => setShowExamples(!showExamples)}
									className="w-full flex items-center justify-between p-3 text-left hover:bg-amber-500/10 transition-colors"
								>
									<div className="flex items-center gap-2">
										<Lightbulb className="w-4 h-4 text-amber-400" />
										<span className="text-textPrimary font-medium text-sm">Exemplos Populares</span>
										<span className="text-grayText text-xs">(clique para {showExamples ? 'ocultar' : 'ver'})</span>
									</div>
									<ChevronDown className={`w-4 h-4 text-amber-400 transition-transform duration-200 ${showExamples ? 'rotate-180' : ''}`} />
								</button>
								<div className={`transition-all duration-300 ease-in-out ${showExamples ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 pt-2">
									{/* Verificar CEP */}
									{(() => {
										const isAlreadyAdded = functions.some(f => f.name === 'verificar_cep');
										return (
											<button
												type="button"
												onClick={() => !isAlreadyAdded && addExampleFunction('verificar_cep')}
												disabled={isAlreadyAdded}
												className={`p-3 rounded-lg text-left transition-all group ${
													isAlreadyAdded
														? 'bg-grayContent/20 border border-grayLight/20 opacity-50 cursor-not-allowed'
														: 'bg-whiteBase/5 border border-amber-500/20 hover:bg-amber-500/10 hover:border-amber-500/40'
												}`}
											>
												<div className="flex items-center gap-2 mb-1">
													<div className={`flex items-center justify-center w-7 h-7 rounded-lg ${
														isAlreadyAdded
															? 'bg-grayLight/20'
															: 'bg-amber-500/20'
													}`}>
														<MapPin className={`w-3.5 h-3.5 ${isAlreadyAdded ? 'text-grayText' : 'text-amber-400'}`} />
													</div>
													<span className={`font-medium text-sm ${
														isAlreadyAdded ? 'text-grayText' : 'text-textPrimary'
													}`}>
														Verificar CEP {isAlreadyAdded && '✓'}
													</span>
												</div>
												<p className={`text-xs ${isAlreadyAdded ? 'text-grayText' : 'text-textSecondary'}`}>
													Consulta endereço através do CEP
												</p>
											</button>
										);
									})()}

									{/* Consultar Feriados */}
									{(() => {
										const isAlreadyAdded = functions.some(f => f.name === 'consultar_feriados');
										return (
											<button
												type="button"
												onClick={() => !isAlreadyAdded && addExampleFunction('consultar_feriados')}
												disabled={isAlreadyAdded}
												className={`p-3 rounded-lg text-left transition-all group ${
													isAlreadyAdded
														? 'bg-grayContent/20 border border-grayLight/20 opacity-50 cursor-not-allowed'
														: 'bg-whiteBase/5 border border-amber-500/20 hover:bg-amber-500/10 hover:border-amber-500/40'
												}`}
											>
												<div className="flex items-center gap-2 mb-1">
													<div className={`flex items-center justify-center w-7 h-7 rounded-lg ${
														isAlreadyAdded
															? 'bg-grayLight/20'
															: 'bg-amber-500/20'
													}`}>
														<Calendar className={`w-3.5 h-3.5 ${isAlreadyAdded ? 'text-grayText' : 'text-amber-400'}`} />
													</div>
													<span className={`font-medium text-sm ${
														isAlreadyAdded ? 'text-grayText' : 'text-textPrimary'
													}`}>
														Consultar Feriados {isAlreadyAdded && '✓'}
													</span>
												</div>
												<p className={`text-xs ${isAlreadyAdded ? 'text-grayText' : 'text-textSecondary'}`}>
													Feriados nacionais do Brasil
												</p>
											</button>
										);
									})()}

									{/* Consultar CNPJ */}
									{(() => {
										const isAlreadyAdded = functions.some(f => f.name === 'consultar_cnpj');
										return (
											<button
												type="button"
												onClick={() => !isAlreadyAdded && addExampleFunction('consultar_cnpj')}
												disabled={isAlreadyAdded}
												className={`p-3 rounded-lg text-left transition-all group ${
													isAlreadyAdded
														? 'bg-grayContent/20 border border-grayLight/20 opacity-50 cursor-not-allowed'
														: 'bg-whiteBase/5 border border-amber-500/20 hover:bg-amber-500/10 hover:border-amber-500/40'
												}`}
											>
												<div className="flex items-center gap-2 mb-1">
													<div className={`flex items-center justify-center w-7 h-7 rounded-lg ${
														isAlreadyAdded
															? 'bg-grayLight/20'
															: 'bg-amber-500/20'
													}`}>
														<Building2 className={`w-3.5 h-3.5 ${isAlreadyAdded ? 'text-grayText' : 'text-amber-400'}`} />
													</div>
													<span className={`font-medium text-sm ${
														isAlreadyAdded ? 'text-grayText' : 'text-textPrimary'
													}`}>
														Consultar CNPJ {isAlreadyAdded && '✓'}
													</span>
												</div>
												<p className={`text-xs ${isAlreadyAdded ? 'text-grayText' : 'text-textSecondary'}`}>
													Dados de empresa pelo CNPJ
												</p>
											</button>
										);
									})()}
									</div>
								</div>
							</div>
						)}

						{/* Botão para criar nova function */}
						{!isCreating && !editingFunction && (
							<div className="flex justify-between items-center">
								<h4 className="text-sm font-medium text-textPrimary">
									Funções Cadastradas ({functions.length})
								</h4>
								<button
									type="button"
									onClick={() => setIsCreating(true)}
									className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40 text-amber-300 rounded-lg font-medium transition-all hover:from-amber-500/30 hover:to-orange-500/30 hover:border-amber-400/60"
								>
									<Plus className="h-4 w-4" />
									<span>Nova Função</span>
								</button>
							</div>
						)}

						{/* Formulário de criação */}
						{isCreating && (
							<div className="bg-grayContent/30 backdrop-blur-xl border border-amber-500/20 rounded-lg p-5">
								{renderFunctionForm(
									newFunction,
									setNewFunction,
									handleCreateFunction,
									() => setIsCreating(false)
								)}
							</div>
						)}

						{/* Formulário de edição */}
						{editingFunction && (
							<div className="bg-grayContent/30 backdrop-blur-xl border border-amber-500/20 rounded-lg p-5">
								{renderFunctionForm(
									editingFunction,
									setEditingFunction,
									handleUpdateFunction,
									() => setEditingFunction(null),
									true
								)}
							</div>
						)}

						{/* Lista de functions */}
						{!isCreating && !editingFunction && (
							<div className="relative z-10 bg-grayBase/80 rounded-lg p-4 space-y-3 shadow-inner border border-amber-500/20 backdrop-blur-sm">
								{functions.length === 0 ? (
									<div className="text-center py-6 text-amber-300/50 border border-dashed border-amber-500/30 rounded-lg">
										<Globe className="w-8 h-8 mx-auto mb-2 opacity-50" />
										<p className="text-sm">Nenhuma função cadastrada</p>
										<p className="text-xs mt-1">Clique em "Nova Função" para começar</p>
									</div>
								) : (
									functions.map((func) => (
										<div key={func.id} className="flex items-center justify-between p-3 bg-whiteBase/5 backdrop-blur-xl rounded-lg border border-amber-500/20 hover:border-amber-500/40 transition-all">
											<div className="flex-1 min-w-0">
												<div className="flex items-center gap-2">
													<span className="text-sm font-medium text-textPrimary truncate font-mono">
														{func.name}
													</span>
													<span className="px-1.5 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded">
														{func.method}
													</span>
													<span className="px-1.5 py-0.5 text-xs bg-emeraldPrimary/20 text-emeraldLight rounded flex items-center gap-1">
														<span className="w-1.5 h-1.5 bg-emeraldLight rounded-full animate-pulse"></span>
														Ativo
													</span>
												</div>
												<p className="text-xs text-grayText truncate mt-0.5">
													{func.description}
												</p>
												{func.parameters.length > 0 && (
													<p className="text-xs text-amber-400/60 mt-0.5">
														{func.parameters.length} parâmetro{func.parameters.length !== 1 ? 's' : ''}
													</p>
												)}
											</div>
											<div className="flex items-center gap-1 ml-2">
												<button
													type="button"
													onClick={() => handleTestFunction(func)}
													className="p-1.5 rounded-lg text-emeraldLight hover:bg-emeraldPrimary/20 transition-colors"
													title="Testar função"
												>
													<Play className="w-4 h-4" />
												</button>
												<button
													type="button"
													onClick={() => handleEditFunction(func)}
													className="p-1.5 rounded-lg text-amber-400 hover:bg-amber-500/20 transition-colors"
													title="Editar função"
												>
													<Edit3 className="w-4 h-4" />
												</button>
												<button
													type="button"
													onClick={() => handleDeleteFunction(func.id)}
													className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/20 transition-colors"
													title="Remover função"
												>
													<Trash2 className="w-4 h-4" />
												</button>
											</div>
										</div>
									))
								)}
							</div>
						)}
					</div>
				</div>

				{/* Botões de ação fixos na parte inferior */}
				{!isCreating && !editingFunction && (
					<div className="flex-shrink-0 flex justify-start px-6 pb-6 pt-4 border-t border-modalOverlayMedium relative z-10">
						<button
							type="button"
							onClick={() => {
								if (onReturnToMain) {
									onReturnToMain();
								} else {
									onClose();
								}
							}}
							className="flex items-center gap-2 rounded-lg text-textPrimary px-4 py-2.5 font-medium transition-all duration-300 bg-gradient-to-r from-dangerFrom to-dangerTo hover:from-redMedium hover:to-redBase shadow-lg hover:shadow-dangerFrom/25"
						>
							<ArrowLeft className="h-4 w-4" />
							<span>Voltar</span>
						</button>
					</div>
				)}
			</DialogContent>

			{testingFunction && (
				<Dialog open={!!testingFunction} onOpenChange={closeTestModal}>
					<DialogContent
						className="max-w-[500px] bg-gradient-to-br from-modalBg via-modalBgSecondary to-modalBg backdrop-blur-3xl border border-modalBorder shadow-2xl shadow-blackBase/50 relative overflow-hidden"
						style={{
							position: 'fixed',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							zIndex: 10001
						}}
					>
						<div className="absolute top-16 left-24 w-32 h-32 bg-emeraldPrimary/10 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '8s' }}></div>

						<button
							type="button"
							onClick={closeTestModal}
							className="absolute right-4 top-4 z-50 flex items-center justify-center w-8 h-8 rounded-full bg-modalOverlayMedium backdrop-blur-xl border border-modalOverlayStrong text-textOnDarkMedium hover:text-textPrimary hover:bg-modalOverlayStrong transition-all duration-300 hover:scale-110"
						>
							<X className="w-4 h-4" />
						</button>

						<DialogHeader className="relative z-10">
							<DialogTitle className="flex items-center gap-4 text-textPrimary">
								<div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-emeraldPrimary/40 via-emeraldMedium/30 to-emeraldDeep/40 shadow-lg border border-emeraldMedium/60">
									<Play className="w-6 h-6 text-emeraldLight drop-shadow-sm" />
								</div>
								<div>
									<h2 className="text-xl font-semibold">Testar Função</h2>
									<p className="text-sm text-textSecondary font-normal font-mono">{testingFunction.name}</p>
								</div>
							</DialogTitle>
						</DialogHeader>

						<div className="space-y-4 py-4 relative z-10">
							<div className="bg-grayContent/30 rounded-lg p-3 border border-modalOverlayMedium">
								<p className="text-sm text-textSecondary mb-1">{testingFunction.description}</p>
								<p className="text-xs text-grayText font-mono">{testingFunction.url}</p>
							</div>

							{testingFunction.parameters && testingFunction.parameters.length > 0 && (
								<div className="space-y-3">
									<h4 className="text-sm font-medium text-textPrimary flex items-center gap-2">
										<Settings className="w-4 h-4 text-amber-400" />
										Parâmetros de Teste
									</h4>
									{testingFunction.parameters.map((param) => (
										<div key={param.name} className="space-y-1">
											<label className="flex items-center gap-2 text-xs text-textSecondary">
												<span>{param.name}</span>
												{param.required && <span className="text-redBase">*</span>}
												<span className="text-grayText">({param.type})</span>
											</label>
											<input
												type={param.type === 'number' ? 'number' : 'text'}
												value={testParams[param.name] || ''}
												onChange={(e) => setTestParams({ ...testParams, [param.name]: e.target.value })}
												placeholder={param.description || param.name}
												className="w-full rounded-lg border border-amber-500/25 bg-grayContent/30 backdrop-blur-xl px-3 py-2 text-textPrimary text-sm focus:border-amber-500/50 focus:ring focus:ring-amber-500/30 placeholder-grayText transition-all"
											/>
										</div>
									))}
								</div>
							)}

							{testResult && (
								<div className={`rounded-lg p-3 border ${
									testResult.success
										? 'bg-emeraldPrimary/10 border-emeraldPrimary/30'
										: 'bg-redBase/10 border-redBase/30'
								}`}>
									<div className="flex items-center gap-2 mb-2">
										{testResult.success ? (
											<CheckCircle className="w-4 h-4 text-emeraldLight" />
										) : (
											<AlertCircle className="w-4 h-4 text-redBase" />
										)}
										<span className={`font-medium text-sm ${testResult.success ? 'text-emeraldLight' : 'text-redBase'}`}>
											{testResult.success ? 'Sucesso!' : 'Erro'}
										</span>
										{testResult.statusCode && (
											<span className="text-xs text-grayText ml-auto">
												Status: {testResult.statusCode}
											</span>
										)}
									</div>
									{testResult.error && (
										<p className="text-sm text-redBase/80">{testResult.error}</p>
									)}
									{testResult.data && (
										<div className="mt-2">
											<p className="text-xs text-grayText mb-1">Resposta:</p>
											<pre className="text-xs text-textPrimary bg-blackBase/30 rounded-lg p-3 overflow-auto max-h-48 whitespace-pre-wrap">
												{typeof testResult.data === 'string'
													? testResult.data
													: JSON.stringify(testResult.data, null, 2)}
											</pre>
										</div>
									)}
								</div>
							)}

							<div className="flex gap-3 pt-4 border-t border-modalOverlayMedium">
								<button
									type="button"
									onClick={closeTestModal}
									className="flex-1 rounded-lg text-textPrimary px-4 py-2.5 font-medium transition-all bg-gradient-to-r from-dangerFrom to-dangerTo hover:from-redMedium hover:to-redBase"
								>
									Cancelar
								</button>
								<button
									type="button"
									onClick={executeTest}
									disabled={isTesting}
									className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emeraldPrimary/80 to-emeraldMedium/80 border border-emeraldLight/60 px-4 py-2.5 text-sm font-semibold text-textPrimary transition-all hover:from-emeraldMedium/90 hover:to-emeraldDeep/90 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{isTesting ? (
										<>
											<Loader2 className="h-4 w-4 animate-spin" />
											Testando...
										</>
									) : (
										<>
											<Play className="h-4 w-4" />
											Executar Teste
										</>
									)}
								</button>
							</div>
						</div>
					</DialogContent>
				</Dialog>
			)}
		</Dialog>
	);
};
